/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Submit Classroom create/join/lifecycle, member-removal, and CL-F03 access-grant commands with revision-safe cache updates.
 * @state-owner TanStack Query mutation state, active/archived Classroom projections, and lifecycle invalidation
 * @allowed-imports app environment; TanStack Query; auth module public API; same-module request helpers, query keys, models, and schemas
 * @forbidden routes; components; direct generated SDK
 * @public-api classrooms module only
 * @side-effects authenticated Classroom mutations plus scoped TanStack Query updates or invalidation, including roster refresh after removal
 * @naming-hook GET -> use<Resource> (no verb); POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-hook PUT/PATCH entity -> useUpdate<Resource>; DELETE -> useDelete<Resource>
 * @naming-hook banned prefixes: useGet, useFetch, useLoad, useSet, useRemove, useSave, useDo
 * @naming-path plural lowercase nouns; /me for the caller; no version, role or verb segment; filters in the query string
 * @naming-body camelCase; never the caller's role and never a server-side key — the token says who, the path says what
 * @naming-rules docs/architecture/API_NAMING.md, all three enforced by pnpm api-naming:check
 * @tests modules/classrooms/components/ClassroomCreateAction.test.tsx, modules/classrooms/components/ClassroomLifecycleAction.test.tsx, modules/classrooms/components/ClassroomMemberManagement.test.tsx, and modules/classrooms/api/access-grants.test.ts
 * @security-boundary browser commands to authenticated Classroom APIs
 * @data-classification Classroom names, invite addresses, join codes, and opaque idempotency keys
 * @trust-model UI input and active context are untrusted hints; backend schema and authorization remain authoritative
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useWorkspaceEnvironment } from '../../../app/config/environment';
import { useActiveOrganizationId } from '../../../app/providers/ActiveRoleProvider';
import { hasHttpStatus } from '../../../shared/lib/api-error';
import { useActiveContext } from '../../auth';
import type { ClassroomAccessGrant } from '../model/access-grants';
import { type ClassroomSummary, mapExistingClassResponse } from '../model/types';
import type { CreateClassValues, JoinClassValues } from '../schemas/class-actions';
import type { InviteValues } from '../schemas/invite';
import {
  createClassroomInvitationRequest,
  createStudentJoinLinkRequest,
  mapAccessGrant,
  resendClassroomInvitationRequest,
  revokeClassroomInvitationRequest,
  updateStudentJoinLinkRequest,
} from './access-grants';
import {
  classroomCreateRequest,
  classroomLeaveRequest,
  classroomMemberRemovalRequest,
  classroomTransitionRequest,
  classroomUpdateRequest,
} from './classroom-directory';
import { classroomKeys } from './classrooms.queries';

function demoAccessGrant(
  adapter: { read(name: string): unknown } | undefined,
  kind: ClassroomAccessGrant['kind'],
  status?: ClassroomAccessGrant['status'],
) {
  const fixture = adapter?.read('classroomAccessGrant') as
    | { grants?: ClassroomAccessGrant[] }
    | undefined;
  const grant = fixture?.grants?.find(
    (item) => item.kind === kind && (status === undefined || item.status === status),
  );
  if (!grant) return null;
  return {
    ...grant,
    token: `${grant.id}-demo-token`,
  };
}

export function useInviteClassMember() {
  const environment = useWorkspaceEnvironment();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      classId,
      values,
    }: {
      classId: string;
      activeContextId: string;
      values: InviteValues;
    }) => {
      if (environment.demoMode) return { invitations_sent: 1 };
      return environment.apiClient.request<{ invitations_sent: number }>(
        `/api/classes/${classId}/invitations`,
        {
          method: 'POST',
          body: { emails: [values.email], role: values.role },
        },
      );
    },
    onSuccess: (_result, input) => {
      queryClient.invalidateQueries({ queryKey: classroomKeys.all(input.activeContextId) });
    },
  });
}

export function useCreateClass() {
  const environment = useWorkspaceEnvironment();
  const activeContextQuery = useActiveContext();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      activeContextId: _activeContextId,
      idempotencyKey,
      values,
    }: {
      activeContextId: string;
      idempotencyKey: string;
      values: CreateClassValues;
    }) => {
      if (environment.demoMode) {
        return mapExistingClassResponse({
          id: `class-${Date.now()}`,
          name: values.name,
          subject: values.subject,
          student_count: 0,
          join_code: `V${Math.random().toString(36).slice(2, 7).toUpperCase()}`,
        });
      }
      const organizationId = activeContextQuery.data?.organizationId;
      if (!organizationId) throw new Error('目前沒有可用的老師工作區');
      const response = await classroomCreateRequest(environment.apiClient, {
        idempotencyKey,
        name: values.name,
        organizationId,
        subject: values.subject || undefined,
      });
      return {
        ...mapExistingClassResponse(response),
        canEditBasicDetails: true,
        canManageLifecycle: true,
      };
    },
    onSuccess: (classItem, input) => {
      queryClient.setQueryData<ClassroomSummary[]>(
        classroomKeys.all(input.activeContextId),
        (current) => (current ? [classItem, ...current] : [classItem]),
      );
    },
  });
}

interface LifecycleMutationInput {
  activeContextId: string;
  classroom: ClassroomSummary;
  idempotencyKey: string;
}

function useOrganizationId(): string | undefined {
  const organizationId = useActiveOrganizationId();
  const activeContext = useActiveContext();
  return organizationId ?? activeContext.data?.organizationId;
}

export function useUpdateClassroom() {
  const environment = useWorkspaceEnvironment();
  const organizationId = useOrganizationId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      activeContextId: _activeContextId,
      classroom,
      idempotencyKey,
      name,
      subject,
    }: LifecycleMutationInput & { name: string; subject: string | null }) => {
      if (environment.demoMode)
        return {
          ...classroom,
          name,
          subject: subject || '未設定科目',
          revision: classroom.revision + 1,
        };
      if (!organizationId) throw new Error('目前沒有可用的老師工作區');
      return mapExistingClassResponse(
        await classroomUpdateRequest(environment.apiClient, classroom.id, {
          expectedRevision: classroom.revision,
          idempotencyKey,
          name,
          organizationId,
          subject,
        }),
      );
    },
    onSuccess: (classroom, input) => {
      queryClient.setQueryData<ClassroomSummary[]>(
        classroomKeys.all(input.activeContextId),
        (current) => current?.map((item) => (item.id === classroom.id ? classroom : item)) ?? [],
      );
    },
    onError: (error, input) => {
      if (!hasHttpStatus(error, 403) && !hasHttpStatus(error, 409)) return;
      void queryClient.invalidateQueries({ queryKey: classroomKeys.all(input.activeContextId) });
    },
  });
}

function useClassroomTransition(transition: 'archive' | 'restore') {
  const environment = useWorkspaceEnvironment();
  const organizationId = useOrganizationId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      activeContextId: _activeContextId,
      classroom,
      idempotencyKey,
    }: LifecycleMutationInput) => {
      if (environment.demoMode)
        return {
          ...classroom,
          status: transition === 'archive' ? ('archived' as const) : ('active' as const),
          revision: classroom.revision + 1,
        };
      if (!organizationId) throw new Error('目前沒有可用的老師工作區');
      return mapExistingClassResponse(
        await classroomTransitionRequest(environment.apiClient, classroom.id, transition, {
          expectedRevision: classroom.revision,
          idempotencyKey,
          organizationId,
        }),
      );
    },
    onSuccess: (classroom, input) => {
      const activeKey = classroomKeys.all(input.activeContextId);
      const archivedKey = classroomKeys.archived(input.activeContextId);
      queryClient.setQueryData<ClassroomSummary[]>(activeKey, (current) =>
        transition === 'archive'
          ? (current ?? []).filter((item) => item.id !== classroom.id)
          : [classroom, ...(current ?? []).filter((item) => item.id !== classroom.id)],
      );
      queryClient.setQueryData<ClassroomSummary[]>(archivedKey, (current) =>
        transition === 'archive'
          ? [classroom, ...(current ?? []).filter((item) => item.id !== classroom.id)]
          : (current ?? []).filter((item) => item.id !== classroom.id),
      );
    },
    onError: (error, input) => {
      if (!hasHttpStatus(error, 403) && !hasHttpStatus(error, 409)) return;
      void queryClient.invalidateQueries({ queryKey: classroomKeys.all(input.activeContextId) });
      void queryClient.invalidateQueries({
        queryKey: classroomKeys.archived(input.activeContextId),
      });
    },
  });
}

export function useArchiveClassroom() {
  return useClassroomTransition('archive');
}

export function useRestoreClassroom() {
  return useClassroomTransition('restore');
}

export function useJoinClass() {
  const environment = useWorkspaceEnvironment();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ values }: { activeContextId: string; values: JoinClassValues }) => {
      if (environment.demoMode) {
        return mapExistingClassResponse({
          id: `joined-${Date.now()}`,
          name: `新加入班級 ${values.joinCode.toUpperCase()}`,
          subject: '未設定科目',
          student_count: 1,
          join_code: values.joinCode.toUpperCase(),
        });
      }
      const response = await environment.apiClient.request<{
        class_id: string;
        class_name: string;
        message: string;
      }>('/api/classes/join', {
        method: 'POST',
        body: { joinCode: values.joinCode.toUpperCase() },
      });
      return mapExistingClassResponse({
        id: response.class_id,
        name: response.class_name,
        join_code: values.joinCode.toUpperCase(),
      });
    },
    onSuccess: (classItem, input) => {
      queryClient.setQueryData<ClassroomSummary[]>(
        classroomKeys.all(input.activeContextId),
        (current) => (current ? [classItem, ...current] : [classItem]),
      );
    },
  });
}

export function useCreateClassroomInvitation() {
  const environment = useWorkspaceEnvironment();
  const queryClient = useQueryClient();
  const organizationId = useOrganizationId();
  return useMutation({
    mutationFn: async ({
      activeContextId: _activeContextId,
      classroomId,
      recipientEmail,
      grantRole,
      expiresAt,
      idempotencyKey,
    }: {
      activeContextId: string;
      classroomId: string;
      recipientEmail: string;
      grantRole: 'teacher' | 'student';
      expiresAt: string;
      idempotencyKey: string;
    }) => {
      if (environment.demoMode)
        return demoAccessGrant(environment.testAdapter, 'invitation', 'pending');
      if (!organizationId) throw new Error('目前沒有可用的老師工作區');
      return mapAccessGrant(
        await createClassroomInvitationRequest(environment.apiClient, classroomId, {
          organizationId,
          recipientEmail,
          grantRole,
          expiresAt,
          idempotencyKey,
        }),
      );
    },
    onSuccess: (_result, input) => {
      void queryClient.invalidateQueries({
        queryKey: classroomKeys.invitations(input.activeContextId, input.classroomId, 'all'),
      });
    },
  });
}

export function useCreateStudentJoinLink() {
  const environment = useWorkspaceEnvironment();
  const queryClient = useQueryClient();
  const organizationId = useOrganizationId();
  return useMutation({
    mutationFn: async ({
      activeContextId: _activeContextId,
      classroomId,
      expiresAt,
      maxUses,
      idempotencyKey,
    }: {
      activeContextId: string;
      classroomId: string;
      expiresAt: string;
      maxUses?: number | null;
      idempotencyKey: string;
    }) => {
      if (environment.demoMode)
        return demoAccessGrant(environment.testAdapter, 'student_join_link', 'pending');
      if (!organizationId) throw new Error('目前沒有可用的老師工作區');
      return mapAccessGrant(
        await createStudentJoinLinkRequest(environment.apiClient, classroomId, {
          organizationId,
          expiresAt,
          ...(maxUses == null ? {} : { maxUses }),
          idempotencyKey,
        }),
      );
    },
    onSuccess: (_result, input) => {
      void queryClient.invalidateQueries({
        queryKey: classroomKeys.invitations(input.activeContextId, input.classroomId, 'all'),
      });
      void queryClient.invalidateQueries({
        queryKey: classroomKeys.studentJoinLinks(input.activeContextId, input.classroomId),
      });
    },
  });
}

export function useSendClassroomInvitation() {
  const environment = useWorkspaceEnvironment();
  const queryClient = useQueryClient();
  const organizationId = useOrganizationId();
  return useMutation({
    mutationFn: async ({
      activeContextId: _activeContextId,
      classroomId,
      invitationId,
      expectedRevision,
      idempotencyKey,
    }: {
      activeContextId: string;
      classroomId: string;
      invitationId: string;
      expectedRevision: number;
      idempotencyKey: string;
    }) => {
      if (environment.demoMode)
        return demoAccessGrant(environment.testAdapter, 'invitation', 'pending');
      if (!organizationId) throw new Error('目前沒有可用的老師工作區');
      return mapAccessGrant(
        await resendClassroomInvitationRequest(environment.apiClient, classroomId, invitationId, {
          organizationId,
          expectedRevision,
          idempotencyKey,
        }),
      );
    },
    onSuccess: (_result, input) => {
      void queryClient.invalidateQueries({
        queryKey: classroomKeys.invitations(input.activeContextId, input.classroomId, 'all'),
      });
    },
  });
}

export function useDeleteClassroomInvitation() {
  const environment = useWorkspaceEnvironment();
  const queryClient = useQueryClient();
  const organizationId = useOrganizationId();
  return useMutation({
    mutationFn: async ({
      activeContextId: _activeContextId,
      classroomId,
      invitationId,
      expectedRevision,
      idempotencyKey,
    }: {
      activeContextId: string;
      classroomId: string;
      invitationId: string;
      expectedRevision: number;
      idempotencyKey: string;
    }) => {
      if (environment.demoMode) {
        const grant = demoAccessGrant(environment.testAdapter, 'invitation', 'pending');
        return grant ? { ...grant, status: 'revoked', token: null } : null;
      }
      if (!organizationId) throw new Error('目前沒有可用的老師工作區');
      return mapAccessGrant(
        await revokeClassroomInvitationRequest(environment.apiClient, classroomId, invitationId, {
          organizationId,
          expectedRevision,
          idempotencyKey,
        }),
      );
    },
    onSuccess: (_result, input) => {
      void queryClient.invalidateQueries({
        queryKey: classroomKeys.invitations(input.activeContextId, input.classroomId, 'all'),
      });
    },
  });
}

export function useDeleteClassroomMember() {
  const environment = useWorkspaceEnvironment();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      classroomId,
      memberUserId,
      idempotencyKey,
    }: {
      activeContextId: string;
      classroomId: string;
      memberUserId: string;
      idempotencyKey: string;
    }) => {
      if (environment.demoMode) {
        const fixture = environment.testAdapter?.read('classroomMembers') as
          | {
              members: Array<{ userId: string }>;
              status?: 'conflict' | 'pending' | 'stale';
            }
          | undefined;
        if (fixture?.status === 'pending') return new Promise(() => undefined);
        if (fixture?.status === 'conflict')
          throw Object.assign(new Error('agent-b-cl-f07 conflict fixture'), { status: 409 });
        if (fixture?.status === 'stale')
          throw Object.assign(new Error('agent-b-cl-f07 stale fixture'), { status: 403 });
        if (fixture) {
          fixture.members = fixture.members.filter((member) => member.userId !== memberUserId);
          environment.testAdapter?.notify('classroomMembers');
        }
        return {
          classroomMemberId: `${classroomId}-${memberUserId}`,
          classroomId,
          userId: memberUserId,
          status: 'removed' as const,
        };
      }
      return classroomMemberRemovalRequest(environment.apiClient, classroomId, memberUserId, {
        idempotencyKey,
      });
    },
    onSuccess: (_result, input) => {
      void queryClient.invalidateQueries({
        queryKey: classroomKeys.members(input.activeContextId, input.classroomId),
      });
      void queryClient.invalidateQueries({
        queryKey: classroomKeys.studentMembers(input.activeContextId, input.classroomId),
      });
      void queryClient.invalidateQueries({ queryKey: classroomKeys.all(input.activeContextId) });
    },
  });
}

export function useDeleteClassroomMembership() {
  const environment = useWorkspaceEnvironment();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      activeContextId: _activeContextId,
      classroomId,
      idempotencyKey,
    }: {
      activeContextId: string;
      classroomId: string;
      idempotencyKey: string;
    }) => {
      if (environment.demoMode) {
        const fixture = environment.testAdapter?.read('classroomLeave') as
          | {
              classrooms: Array<{ id: string }>;
              status:
                | 'conflict'
                | 'denied'
                | 'error'
                | 'loading'
                | 'not-found'
                | 'pending'
                | 'ready'
                | 'stale';
            }
          | undefined;
        if (fixture?.status === 'pending' || fixture?.status === 'loading')
          return new Promise(() => undefined);
        if (fixture?.status === 'conflict')
          throw Object.assign(new Error('agent-b-cl-f13 conflict fixture'), { status: 409 });
        if (fixture?.status === 'not-found')
          throw Object.assign(new Error('agent-b-cl-f13 not-found fixture'), { status: 404 });
        if (fixture?.status === 'stale')
          throw Object.assign(new Error('agent-b-cl-f13 stale fixture'), { status: 403 });
        if (fixture) {
          fixture.classrooms = fixture.classrooms.filter(
            (classroom) => classroom.id !== classroomId,
          );
          environment.testAdapter?.notify('classroomLeave');
        }
        return classroomId;
      }
      await classroomLeaveRequest(environment.apiClient, classroomId, { idempotencyKey });
      return classroomId;
    },
    onSuccess: (_classroomId, input) => {
      void queryClient.invalidateQueries({ queryKey: classroomKeys.all(input.activeContextId) });
    },
  });
}

export function useUpdateStudentJoinLink() {
  const environment = useWorkspaceEnvironment();
  const queryClient = useQueryClient();
  const organizationId = useOrganizationId();
  return useMutation({
    mutationFn: async ({
      activeContextId: _activeContextId,
      classroomId,
      joinLinkId,
      expectedRevision,
      expiresAt,
      maxUses,
      state,
      idempotencyKey,
    }: {
      activeContextId: string;
      classroomId: string;
      joinLinkId: string;
      expectedRevision: number;
      expiresAt?: string;
      maxUses?: number | null;
      state?: 'revoked';
      idempotencyKey: string;
    }) => {
      if (environment.demoMode) {
        const grant = demoAccessGrant(environment.testAdapter, 'student_join_link', 'pending');
        return grant ? { ...grant, revision: grant.revision + 1 } : null;
      }
      if (!organizationId) throw new Error('目前沒有可用的老師工作區');
      return mapAccessGrant(
        await updateStudentJoinLinkRequest(environment.apiClient, classroomId, joinLinkId, {
          organizationId,
          expectedRevision,
          ...(expiresAt ? { expiresAt } : {}),
          ...(maxUses === undefined ? {} : { maxUses }),
          ...(state ? { state } : {}),
          idempotencyKey,
        }),
      );
    },
    onSuccess: (_result, input) => {
      void queryClient.invalidateQueries({
        queryKey: classroomKeys.invitations(input.activeContextId, input.classroomId, 'all'),
      });
      void queryClient.invalidateQueries({
        queryKey: classroomKeys.studentJoinLinks(input.activeContextId, input.classroomId),
      });
    },
  });
}
